import {
    IgAccount,
    IgMetadataAge,
    IgMetadataColor,
    IgMedia,
    IgMetadataEmotion,
    IgMetadataFaceDetail,
    IgMetadataGender,
    IgMetadataHashtag,
    IgAccTime,
    IgMediaTime,
} from '@core/models/ApiFields/ig-entities';
import { TimeFields } from '@core/models/ApiFields/time-fields.enums';
import { CHART_TYPE } from '@features/social-analysis/models/IChart';
import { DimCategory, CategType } from '@features/social-analysis/models/IDimensions';

export const timeDims = {
    display: 'Time',
    type: CategType.TIME,
    values: [
        { display: 'Day', value: TimeFields.date, type: CHART_TYPE.LINE },
        { display: 'Week', value: TimeFields.week, type: CHART_TYPE.LINE },
        { display: 'Month', value: TimeFields.month, type: CHART_TYPE.LINE },
        { display: 'Weekday', value: TimeFields.weekday, type: CHART_TYPE.BAR },
    ],
};

export const infoDims = {
    display: 'Info',
    type: CategType.INFO,
    values: [
        {
            display: 'Account',
            value: IgAccount.getPrefixed('igAccount'),
            fixed: true,
            prefix: IgAccount.prefix,
            type: CHART_TYPE.BAR,
        },
        {
            display: 'Age',
            value: IgMetadataAge.getPrefixed('age'),
            prefix: IgMetadataAge.prefix,
            type: CHART_TYPE.BAR,
        },
        {
            display: 'Color',
            value: IgMetadataColor.getPrefixed('color'),
            prefix: IgMetadataColor.prefix,
            type: CHART_TYPE.BAR,
        },
        {
            display: 'Content type',
            value: IgMedia.getPrefixed('contentType'),
            prefix: IgMedia.prefix,
            type: CHART_TYPE.BAR,
        },
        {
            display: 'Emotion',
            value: IgMetadataEmotion.getPrefixed('emotion'),
            prefix: IgMetadataEmotion.prefix,
            type: CHART_TYPE.BAR,
        },
        {
            display: 'Face detail',
            value: IgMetadataFaceDetail.getPrefixed('faceDetail'),
            prefix: IgMetadataFaceDetail.prefix,
            type: CHART_TYPE.BAR,
        },
        {
            display: 'Gender',
            value: IgMetadataGender.getPrefixed('gender'),
            prefix: IgMetadataGender.prefix,
            type: CHART_TYPE.BAR,
        },
        {
            display: 'Hashtag',
            value: IgMetadataHashtag.getPrefixed('hashtag'),
            prefix: IgMetadataHashtag.prefix,
            type: CHART_TYPE.BAR,
        },
        {
            display: 'Post type',
            value: IgMedia.getPrefixed('mediaType'),
            prefix: IgMedia.prefix,
            type: CHART_TYPE.BAR,
        },
    ],
};

export const insightsDims: DimCategory[] = [timeDims, infoDims];

export const mediaMetrics: DimCategory = {
    display: 'Media',
    values: [
        {
            display: 'Comments',
            value: IgMediaTime.getPrefixed('comments'),
            prefix: IgMediaTime.prefix,
        },
        {
            display: 'Followers from media',
            value: IgMediaTime.getPrefixed('followers'),
            prefix: IgMediaTime.prefix,
        },
        {
            display: 'Interactions from media',
            value: IgMediaTime.getPrefixed('interactions'),
            prefix: IgMediaTime.prefix,
        },
        {
            display: 'Likes',
            value: IgMediaTime.getPrefixed('likes'),
            prefix: IgMediaTime.prefix,
            defaultCol: true,
        },
        {
            display: 'Media count',
            value: IgMedia.getPrefixed('mediaCount'),
            prefix: IgMedia.prefix,
            defaultCol: true,
        },
        {
            display: 'Negative captions',
            value: IgMedia.getPrefixed('negativeCaptions'),
            prefix: IgMedia.prefix,
        },
        {
            display: 'Negative comments',
            value: IgMediaTime.getPrefixed('negativeComments'),
            prefix: IgMediaTime.prefix,
        },
        {
            display: 'Positive captions',
            value: IgMedia.getPrefixed('positiveCaptions'),
            prefix: IgMedia.prefix,
        },
        {
            display: 'Positive comments',
            value: IgMediaTime.getPrefixed('positiveComments'),
            prefix: IgMediaTime.prefix,
        },
        {
            display: 'Profile activity from media',
            value: IgMediaTime.getPrefixed('profileActivity'),
            prefix: IgMediaTime.prefix,
        },
        {
            display: 'Profile visits from media',
            value: IgMediaTime.getPrefixed('profileViews'),
            prefix: IgMediaTime.prefix,
        },
    ],
};

export const accountMetrics: DimCategory = {
    display: 'Account',
    values: [
        {
            display: 'Accounts engaged',
            value: IgAccTime.getPrefixed('accountsEngaged'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Follows',
            value: IgAccTime.getPrefixed('follows'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Followers',
            value: IgAccTime.getPrefixed('followers'),
            prefix: IgAccTime.prefix,
            defaultCol: true,
        },
        {
            display: 'Followers left IG',
            value: IgAccTime.getPrefixed('followersUnknown'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile activity',
            value: IgAccTime.getPrefixed('profileActivity'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile book now',
            value: IgAccTime.getPrefixed('profileActivityBookNow'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile call',
            value: IgAccTime.getPrefixed('profileActivityCall'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile direction',
            value: IgAccTime.getPrefixed('profileActivityDirection'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile email',
            value: IgAccTime.getPrefixed('profileActivityEmail'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile instant experience',
            value: IgAccTime.getPrefixed('profileActivityInstantExperience'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile other',
            value: IgAccTime.getPrefixed('profileActivityOther'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile text',
            value: IgAccTime.getPrefixed('profileActivityText'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Profile views',
            value: IgAccTime.getPrefixed('profileViews'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Reach followers left IG (%)',
            value: IgAccTime.getPrefixed('reachUnknownPercentage'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Reach followers (%)',
            value: IgAccTime.getPrefixed('reachFollowerPercentage'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Reach followers',
            value: IgAccTime.getPrefixed('reachFollower'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Reach non followers (%)',
            value: IgAccTime.getPrefixed('reachNonFollowerPercentage'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Reach non followers',
            value: IgAccTime.getPrefixed('reachNonFollower'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Unfollowers',
            value: IgAccTime.getPrefixed('unfollowers'),
            prefix: IgAccTime.prefix,
        },
        {
            display: 'Website clicks',
            value: IgAccTime.getPrefixed('websiteClicks'),
            prefix: IgAccTime.prefix,
        },
    ],
};

export const insightsMetrics = [mediaMetrics, accountMetrics];
